import type { GQL_ID, NestedFilter } from '@@partner:graphql/types'
import type { FilterConfig } from '@components/common/filter/FilterConfig'

import React, { useCallback } from 'react'
import FilterPanel from '@components/common/filter/FilterPanel'
import { createFilter } from '@components/common/filter/FilterConfig'
import { prepareOrdersFilter } from '@@partner:helpers/order'
import { normalizePhoneNumber } from '@@partner:helpers/util'
import AddressAutosuggest from '../form/AddressAutoSuggest'

import {
  ShopsPicker,
  AssemblersPicker,
  CouriersPicker,
  BusinessProfilesPicker,
  OrderStatusesPicker
} from '@components/common/pickers'

import {
  ORDER_CREATE_TYPE_WEB,
  ORDER_CREATE_TYPE_OPERATOR
} from '@@partner:config/order-create-types'

export interface OrdersFiltersType {
  address: string
  ids: string
  phone: string
  from?: number
  to?: number
  slotFrom?: number
  slotTo?: number
  statuses: number[]
  shops: GQL_ID[]
  assemblers: GQL_ID[]
  couriers: GQL_ID[]
  createdByType: string
  paymentType: string
  hasFeedback?: boolean
  business_profile_id: GQL_ID[]
}

export interface OrdersFilterProps {
  onFilterChanged: (filters: NestedFilter) => void
}

const ORDER_CREATED_BY_TYPES = [
  { label: 'Сайт', value: ORDER_CREATE_TYPE_WEB },
  { label: 'Оператор', value: ORDER_CREATE_TYPE_OPERATOR }
]

const ORDER_PAYMENT_TYPES = [
  { label: 'Картой онлайн', value: 'yandex' },
  { label: 'Наличными', value: 'cash' }
]

const defaultFilter: OrdersFiltersType = {
  address: '',
  ids: '',
  phone: '',
  from: undefined,
  to: undefined,
  slotFrom: undefined,
  slotTo: undefined,
  statuses: [],
  shops: [],
  assemblers: [],
  couriers: [],
  createdByType: '',
  paymentType: '',
  hasFeedback: false,
  business_profile_id: []
}

const filterConfig: FilterConfig[] = [
  // Основные фильтры
  createFilter.text('ids', 'Номера заказов'),
  createFilter.text('phone', 'Телефон клиента'),
  createFilter.dateRange('deliverySlot', 'Слот доставки'),
  createFilter.custom('statuses', 'Статусы', ({ value, onChange }) => (
    <OrderStatusesPicker value={value ?? []} onChange={onChange} />
  ), { valueType: 'number[]' }),
  createFilter.custom('shops', 'Магазины', ({ value, onChange }) => (
    <ShopsPicker searchable={true} multiple value={value ?? []} onChange={onChange} />
  ), { valueType: 'string[]' }),
  
  // Дополнительные фильтры
  createFilter.custom('assemblers', 'Сборщики', ({ value, onChange }) => (
    <AssemblersPicker value={value ?? []} onChange={onChange} />
  ), { expandable: true, section: 'Сотрудники', valueType: 'string[]' }),
  createFilter.custom('couriers', 'Курьеры', ({ value, onChange }) => (
    <CouriersPicker value={value ?? []} onChange={onChange} />
  ), { expandable: true, section: 'Сотрудники', valueType: 'string[]' }),
  
  createFilter.dateRange('placedAt', 'Дата размещения', { expandable: true, section: 'Прочее' }),
  createFilter.select('createdByType', ORDER_CREATED_BY_TYPES, 'Кем создано', { expandable: true, section: 'Прочее' }),
  createFilter.select('paymentType', ORDER_PAYMENT_TYPES, 'Способ оплаты', { expandable: true, section: 'Прочее' }),
  createFilter.checkbox('hasFeedback', 'Только с отзывами', { expandable: true, section: 'Прочее' }),
  createFilter.custom('address', 'Адрес', ({ value, onChange }) => (
    <AddressAutosuggest
      value={value ?? ''}
      onChange={onChange}
      placeholder="Укажите адрес клиента"
      onSuggestionSelected={(_event: any, data: any) => onChange(data?.suggestionValue ?? '')}
      onReset={() => onChange('')}
      inputProps={{
        className: 'rs-input rs-input-sm',
        style: { width: '100%' }
      }}
    />
  ), { expandable: true, section: 'Клиент', valueType: 'string' }),
  createFilter.custom('business_profile_id', 'Бизнес-профиль', ({ value, onChange }) => (
    <BusinessProfilesPicker value={value ?? []} onChange={onChange} />
  ), { expandable: true, section: 'Клиент', valueType: 'string[]' })
]

export default function OrdersFilter ({ onFilterChanged }: OrdersFilterProps) {
  const handleSearch = useCallback((state: OrdersFiltersType) => {
    onFilterChanged(prepareOrdersFilter(state))
  }, [ onFilterChanged ])

  // Преобразование state перед отправкой (нормализация телефона, парсинг дат)
  const transformBeforeSubmit = useCallback((state: OrdersFiltersType): OrdersFiltersType => {
    const placedAt = (state as any).placedAt as { from?: number; to?: number } | undefined
    const deliverySlot = (state as any).deliverySlot as { from?: number; to?: number } | undefined
    return {
      ...state,
      phone: state.phone ? normalizePhoneNumber(state.phone) : '',
      from: placedAt?.from,
      to: placedAt?.to,
      slotFrom: deliverySlot?.from,
      slotTo: deliverySlot?.to
    }
  }, [])

  return (
    <FilterPanel<OrdersFiltersType>
      filters={filterConfig}
      defaultState={defaultFilter}
      onSearch={handleSearch}
      transformBeforeSubmit={transformBeforeSubmit}
    />
  )
}
